import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { SystemAdminService } from '../../../../services/system-admin.service';
import { CommonModule } from '@angular/common';
import { BranchDetailsResponse, CompanyDetailsResponse, CompanyTypeResponse, UserLandingGrids, UserLandingResponse } from '../../../../models/SystemAdmin.model';
import { Model } from '../../../../models/QuoteList';
import { debounceTime, filter, forkJoin, map, Subject, Subscription, switchMap } from 'rxjs';
import { ToastService } from '../../../../services/toast.service';
import { ToastType } from '../../../../enums/config.enum';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss'
})
export class UserListComponent implements OnDestroy {
  router = inject(Router);
  userList = signal<UserLandingGrids[]>([]);
  currentSortField = signal('Date');
  currentSortDirection = signal('Desc');
  displayedPages = signal<number[]>([]);
  selectedCompany = signal<number>(0);
  selectedBranch = signal<number>(0);
  
  searchQuery = signal('');
  searchSubject = new Subject<string>();
  isSearchInputChange = signal<boolean>(false);
  isbranchDisabled = signal<boolean>(false);
  isAdminCompany = signal<boolean>(false);
  currentFilterList: any = [];
  companyList = signal<CompanyDetailsResponse[]>([]);
  branchList = signal<BranchDetailsResponse[]>([]);
  companyType = signal<CompanyTypeResponse[]>([]);
  currentPageNumber = signal<number>(1);
  currentPageSize = signal<number>(10);
  totalPages = signal<number>(0);
  jumpToPage = signal<number | null>(null);
  totalItems = signal<number | null>(null);
  startItem = signal<number | null>(null);
  endItem = signal<number | null>(null);
  systemAdminService = inject(SystemAdminService);
  toastService = inject(ToastService);
  private searchSubscription: Subscription | undefined;

  constructor() {this.setupSearch()}
  ngOnInit(): void {
    this.systemAdminService.removeSysUserEmailID(); // remove sysEmailID from storage
    this.fetchCompanyDropdownAndUserlist();
  }

  fetchAllUserList(currentPageNumber: number, currentPageSize: number) {
    this.systemAdminService.getAllUsersList(currentPageNumber, currentPageSize, this.getAddtnlParams()).subscribe({
      next: (response: UserLandingResponse) => {
        if(response){
          this.userList.set(response.userLandingGrids);
          this.onHandleResponseDataBind(response);
        }
      },
      error: (error) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    })
  }

  fetchCompanyDropdownAndUserlist() {
    forkJoin([
      this.systemAdminService.getCompanyDropdownList(),
      this.systemAdminService.getAllUsersList(this.currentPageNumber(), this.currentPageSize(), this.getAddtnlParams()),
      this.systemAdminService.getAllCompanyTypes(),
    ]).pipe(
      map(([companyList, userList,companyTypes]) => {
        return {companyList, userList,companyTypes};
      })
    ).subscribe({
      next:(response: any) => {
        if(response){
          this.companyList.set(response?.companyList);
          this.userList.set(response?.userList?.userLandingGrids);
          this.companyType.set(response?.companyTypes)
          this.systemAdminService.setCompanyTypeList(response?.companyTypes);
                    this.onHandleResponseDataBind(response?.userList);
        }
      },error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    });
  }

  fetchBranchDropdownAndUserlist() {
    forkJoin([
      this.systemAdminService.getBranchByCompanyId(String(this.selectedCompany())),
      this.systemAdminService.getAllUsersList(this.currentPageNumber(), this.currentPageSize(), this.getAddtnlParams())
    ]).pipe(
      map(([branchList, userList]) => {
        return {branchList, userList};
      })
    ).subscribe({
      next:(response: any) => {
        if(response){
          this.branchList.set(response?.branchList);
          this.userList.set(response?.userList?.userLandingGrids);
          this.onHandleResponseDataBind(response?.userList);
          this.isbranchDisabled.set(false);
        }
      },error: (error: any) => {
        console.log("error", error);
        this.selectedBranch.set(0); // reset selected branch
        this.branchList.set([]); // empty branchlist
        this.isbranchDisabled.set(true); 
        const msg = error?.status === 404 && error?.statusText === "Not Found" ? "System error. No branches found for the specified company. Please exit and contact help@AbacusAU.com." :
        "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com."
        this.toastService.addNotification({message: msg, type: ToastType.Error});
      }
    });
  }


  getBranchDetailsByCompanyId() {
    this.systemAdminService.getBranchByCompanyId(String(this.selectedCompany())).subscribe({
        next: (response: BranchDetailsResponse[]) => {
            if(response){
              this.branchList.set(response);
              this.isbranchDisabled.set(false);
            }
        },
        error: (error: any) => {
            console.log("error", error);
            this.selectedBranch.set(0); // reset selected branch
            this.branchList.set([]); // empty branchlist
            this.isbranchDisabled.set(true);
            const msg = error?.status === 404 && error?.statusText === "Not Found" ? "System error. No branches found for the specified company. Please exit and contact help@AbacusAU.com." :
            "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com."
            this.toastService.addNotification({message: msg, type: ToastType.Error});
        }
    });
  }

  setupSearch(): void {
    effect(() => {
      const minChars = 3
      this.searchSubscription = this.searchSubject.pipe(
        debounceTime(300), // Wait for 300ms after the last keypress
        filter(query => {
          const queryLength = query.length;
          if(query.trim() && queryLength >= minChars){return true;}
          else{
            this.currentPageNumber.set(1);
            this.jumpToPage.set(null);
            if(this.isSearchInputChange()){
              this.fetchAllUserList(this.currentPageNumber(), this.currentPageSize());
              this.isSearchInputChange.set(false);
            }
            return false;
          }
        }), // Only proceed if query has enough characters
        switchMap(query => {
          const searchBy = query.trim();
          this.currentPageNumber.set(1);
          this.jumpToPage.set(null);
          this.isSearchInputChange.set(true);
          return this.systemAdminService.getAllUsersList(this.currentPageNumber(), this.currentPageSize(), this.getAddtnlParams()) 
        })
      ).subscribe({
        next: (response: any) => {
          if(response){
            this.userList.set(response.userLandingGrids);
            this.onHandleResponseDataBind(response);
          }
        },
        error: (error) => {
          console.log("error", error);
        }
      });
    });
  }

  onHandleResponseDataBind(responseData: UserLandingResponse){
    const {model} = responseData;
    this.currentPageSize.set(model.pageSize);
    this.currentPageNumber.set(model.currentPage);
    this.totalPages.set(model.totalPages);
    this.totalItems.set(model.numOfRecords);
    this.startItem.set((model.currentPage - 1) * model.pageSize + 1);
    const end = model.currentPage * model.pageSize;
    const endRange = end > model.numOfRecords ? model.numOfRecords : end;
    this.endItem.set(endRange);
    this.updateDisplayedPages(model);
  }

  updateDisplayedPages(modelInfo: Model): void {
    const totalPages = modelInfo.totalPages;

    // Calculate the range of pages to display
    const startPage = Math.max(1, modelInfo.currentPage + 1 - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    let pagesToDisplay = [];
    for (let i = startPage; i <= endPage; i++) {
      pagesToDisplay.push(i);
    }
    this.displayedPages.set(pagesToDisplay);
  }

  onSort(field: any, isSortIconUpdated=false): void {
    if (this.currentSortField() === field && isSortIconUpdated) {
      // If the same column is clicked, toggle direction
      this.currentSortDirection.set(this.currentSortDirection() === 'Asc' ? 'Desc' : 'Asc');
    } else if(isSortIconUpdated) {
      // If a new column is clicked, set to ascending by default
      this.currentSortField.set(field);
      this.currentSortDirection.set('Asc');
    }
    const isJumpToPageActive = this.jumpToPage() ? true : false;
    this.onPageSeleted(this.currentPageNumber(), isJumpToPageActive);
  }

  getAddtnlParams(): { [key: string]: string | number | null }{
    const sortParams: { [key: string]: string | number | null } = {sort: this.currentSortDirection(), sortColumn: this.currentSortField()};
    this.selectedCompany() ? sortParams['company'] = this.selectedCompany() : '';
    this.selectedBranch() !== 0 ? sortParams['branch'] = this.selectedBranch() : '';
    if(this.searchQuery() && this.searchQuery().length >= 3){
      sortParams['search'] = this.searchQuery();
    }
    return sortParams;
  }

  onJumpToChange(event: Event): void{
    const input = event.target as HTMLInputElement;
    if (input.value !== '') {
      this.jumpToPage.set(Number(input.value))
    }else{
      this.jumpToPage.set(1); 
    }
  }

  // Method to jump to the specific page
  jumpToSpecificPage() {
    const page = this.jumpToPage();
    if(!this.jumpToPage()){
      return;
    }

    if (page && page >= 1 && page <= this.totalPages()) {
      this.currentPageNumber.set(page);
    } else {
      this.jumpToPage.set(1);
      this.currentPageNumber.set(1);
    }

    this.onPageSeleted(this.currentPageNumber(), true);
  }

  // Validation method to allow only numeric values
  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.key.charCodeAt(0);
    
    // Check if the input is a number or control keys (backspace, delete, etc.)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault(); // Prevent non-numeric input
    }
  }

  
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
    // Emit the new value to the subject
    this.searchSubject.next(input.value);
  }

  onClearSearch() {
    this.searchQuery.set('');
    // Emit the new value to the subject
    this.searchSubject.next('');
  } 

  onPageSeleted(currentPage: number, isJumpToUserUpdate: boolean = false){
    this.fetchAllUserList(currentPage, this.currentPageSize());
    if (!isJumpToUserUpdate) this.jumpToPage.set(null);
  }

  onCompanyChange(event: Event) {
    const selectedCompany = event.target as HTMLSelectElement;
    this.currentPageNumber.set(1);
    this.jumpToPage.set(null);
    this.selectedCompany.set(Number(selectedCompany.value));
    let removeExistingCompanyFilter: { type: string; name: string; }[] = [];
    let selectedCompanyName = this.companyList().filter((company) => String(company.companyId) === selectedCompany.value);
    this.selectedBranch.set(0); //remove the branch filter
    if(selectedCompany.value !== "0"){
      removeExistingCompanyFilter.push({
        type: 'company', name: selectedCompanyName[0].companyName
      }); 
      this.currentFilterList = removeExistingCompanyFilter;
      this.fetchBranchDropdownAndUserlist();
    }else{
      this.currentFilterList = removeExistingCompanyFilter;
      this.selectedCompany.set(0);
      this.branchList.set([]); // empty branch list
      this.fetchAllUserList(this.currentPageNumber(), this.currentPageSize());
    }
  }

  onBranchChange(event: Event) {
    const selectedBranch = event.target as HTMLSelectElement;
    this.currentPageNumber.set(1);
    this.jumpToPage.set(null);
    this.selectedBranch.set(Number(selectedBranch.value));
    let removeExistingBranchFilter = this.currentFilterList.filter((item: any) => item.type !== "branch");
    let selectedBranchName = this.branchList().filter((branch) => String(branch.branchId) === selectedBranch.value);
    if(selectedBranch.value !== "0"){
      removeExistingBranchFilter.push({
        type: 'branch', name: selectedBranchName[0].branchName
      }); 
      this.currentFilterList = removeExistingBranchFilter;
      this.fetchAllUserList(this.currentPageNumber(), this.currentPageSize());
    }else{
      this.currentFilterList = removeExistingBranchFilter;
      this.selectedBranch.set(0);
      this.fetchAllUserList(this.currentPageNumber(), this.currentPageSize());
    }
  }

  onClearCurrentFilter(type:string){
    let removeExistingFilter = this.currentFilterList.filter((item: any) => item.type !== type);
    switch(type){
      case 'company':
        this.selectedCompany.set(0);
        this.selectedBranch.set(0); // reset selected branch
        this.branchList.set([]); // empty branchlist
        break;
      case 'branch':
        this.selectedBranch.set(0);
        break;
    }
    if(type !== 'company'){
      this.currentFilterList = removeExistingFilter;
    }else{
      this.currentFilterList = [];
    }
    this.fetchAllUserList(1, this.currentPageSize());
  }

  onAdminCompanyChange(status: boolean) {
    this.isAdminCompany.set(status);
}

  onClearAllfilter(){
    this.currentFilterList = [];
    this.selectedCompany.set(0);
    this.selectedBranch.set(0);
    this.fetchAllUserList(1, this.currentPageSize());
  }

  addUser(){
    this.router.navigate(['/system-admin-dashboard/user/add-user']);
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();  // Clean up subscription
    }
  }
}
